import {ProductDetailsSection_ArticleDetail_Lane} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/product_details.types';
import {useTrackFeature} from '@aboutyou/tracking';
import {
  Accordion,
  AccordionContentContainer,
  COLORS,
  ErrorBoundary,
  Text,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useState} from 'react';
import {StyledDescriptionList} from '../../ProductDetails/ProductDetailsSections/SectionWrapper';
import {ProductDetailsSection} from '../../ProductDetails/shared/ProductDetailsSection';
import {SizeChartComponentProps} from '../../SizeCharts/SizeCharts';

export const ProductDetailAccordion: React.FC<
  {
    articleNumber?: string;
    section: ProductDetailsSection_ArticleDetail_Lane;
  } & SizeChartComponentProps
> = ({articleNumber, section, SizeChartLayout, SizeSystemLayout}) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const trackFeature = useTrackFeature();

  const handleOnOpen = () => {
    setIsAccordionOpen(true);
  };

  const handleOnClose = () => {
    setIsAccordionOpen(false);
  };

  return (
    <StyledAccordion
      data-testid={section.label}
      $isOpen={isAccordionOpen}
      onClose={handleOnClose}
      onOpen={handleOnOpen}
      alwaysRenderContent={true}
      trackOpen={() => {
        trackFeature({
          eventType: 'click',
          label: `expand|${section.type?.$case}|section`,
        });
      }}
      trackClose={() => {
        trackFeature({
          eventType: 'click',
          label: `collapse|${section.type?.$case}|section`,
        });
      }}
      scrollTitleInView
      content={
        <StyledText
          data-testid="productDetailsOpenerLabel"
          size="px16"
          weight="w500"
          color="Black"
          data-show-ellipsis={!isAccordionOpen}
        >
          {section.label}
        </StyledText>
      }
      toggleContent={
        <StyledProductDetails
          data-testid={`productDetailsAccordionContent_${section.label.replace(
            /\s/g,
            '',
          )}`}
        >
          <ErrorBoundary
            key={`${section.label}_errorBoundary`}
            renderContext="product_details"
          >
            <ProductDetailsSection
              section={section}
              articleNumber={articleNumber}
              hideAdvertisementLaneOnBigScreens
              SizeChartLayout={SizeChartLayout}
              SizeSystemLayout={SizeSystemLayout}
              shouldRenderLabel={false}
            />
          </ErrorBoundary>
        </StyledProductDetails>
      }
    />
  );
};

const StyledProductDetails = styled.div`
  padding: 0;
  & > ${StyledDescriptionList} {
    padding-left: 0;
    padding-right: 0;
  }

  & ul {
    margin-top: 0;
    margin-bottom: 20px;
    color: ${COLORS.Black};

    ${tabletAndUpLinaria('column-count: 1;')}
  }

  & button {
    color: ${COLORS.Black};
  }

  & > ${StyledDescriptionList}:first-child {
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const StyledAccordion = styled(Accordion)<{$isOpen: boolean}>`
  height: 64px;
  padding: 0;
  border: none;
  padding-inline: 8px;

  & ${AccordionContentContainer} {
    margin-top: 0;
    margin-bottom: ${({$isOpen}) => ($isOpen ? '2rem' : 0)};
  }

  &&& {
    border-bottom: solid 0.8px ${COLORS.Grey3};
    border-radius: 0;
    height: 100%;
  }

  & > button {
    height: 64px;
  }
`;

const StyledText = styled(Text)`
  display: flex;
  align-items: center;

  &[data-show-ellipsis='true'] {
    @supports (-webkit-line-clamp: 2) {
      display: -webkit-box;
      overflow: hidden;
      text-overflow: ellipsis;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
  }

  & > svg {
    margin-right: 0.2rem;
  }
`;
